import * as React from 'react'
import { Select, MenuItem, Paper, Button, Chip, Box, Typography } from '@mui/material'
import { StorageOutlined, DataObjectOutlined } from '@mui/icons-material';
import { DebouncedTextField } from '../components/DebouncedTextField';
import { serviceLocator } from '../services';
import { MainPadding } from '../components/MainPadding';
import DangerButton from '../_ho-forms-lib/src/MaterialComponents/DangerButton';
import { AppSection } from '../components/AppSection';

interface Record {
  initialValue: string;
  value: string;
  dirty: boolean;
  valid: boolean;
  _id: string;
}

interface State {
  table: string;
  items: Array<Record>;
  focusedIndex: number;
  tables: string[];
}

const databaseService = serviceLocator.getDatabaseService();

function isValidJson(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

export class Database extends React.Component<any, any> {

  state: State = {
    table: 'websites',
    items: [],
    focusedIndex: -1,
    tables: ['websites'],
  };

  handleItemChange = (e: any) => {
    const value = e.target.value;
    // JSON.parse throws on every keystroke that lands on incomplete JSON
    // (every paste, every backspace mid-quote). The "Invalid JSON" chip is
    // the user-facing signal — don't surface it as an error toast.
    const valid = isValidJson(value);
    const item = this.state.items[this.state.focusedIndex];
    item.dirty = true;
    item.valid = valid;
    item.value = value;
    this.forceUpdate();
  };

  handleFocus = (e: any) => {
    const index = parseInt(e.currentTarget.dataset.index);
    this.setState({ focusedIndex: index });
  };

  loadItems = async () => {
    const items = (await databaseService.list(this.state.table))
      .map(x => {
        const value = JSON.stringify(x);
        return {
          initialValue: value, value, dirty: false, valid: true, _id: x._id,
        };
      });
    this.setState({ items });
  };

  loadTables = async () => {
    const tables = await databaseService.listTables();
    this.setState({ tables });
  };

  handleTableChange = (e: any) => {
    this.setState({ table: e.target.value }, () => {
      this.loadItems();
    });
  };

  componentDidMount = () => {
    this.loadItems();
    this.loadTables();
  };

  handleDelete = (e: any, loaded: boolean) => {
    if (loaded) {
      const index = this.state.focusedIndex;
      const data = this.state.items[index];
      databaseService.delete(this.state.table, data);
      this.state.items.splice(index, 1);
      this.setState({ focusedIndex: -1 });
    }
  };

  handleRevert = () => {
    const index = this.state.focusedIndex;
    const data = this.state.items[index];
    data.value = data.initialValue;
    data.dirty = false;
    data.valid = true;
    this.setState({ focusedIndex: -1 });
  };

  handleUpdate = async () => {
    const index = this.state.focusedIndex;
    const data = this.state.items[index];
    if (data.valid) {
      await databaseService.update(this.state.table, JSON.parse(data.value));
      data.dirty = false;
      data.initialValue = JSON.stringify(JSON.parse(data.value));
      data.value = data.initialValue;
      this.setState({ focusedIndex: -1 });
    }
  };

  render() {
    const dirtyCount = this.state.items.filter(x => x.dirty).length;
    const invalidCount = this.state.items.filter(x => !x.valid).length;

    return (
      <MainPadding>
        <AppSection section="database" />

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography className="eyebrow" sx={{ color: 'text.secondary' }}>
              Administration · raw access
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                fontFamily: '"Inter Tight", "Inter", sans-serif',
                fontWeight: 700,
                letterSpacing: '-0.018em',
                fontSize: { xs: 28, md: 34 },
                lineHeight: 1.1,
              }}
            >
              Database
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 560, lineHeight: 1.55 }}>
              Direct read/write into application collections. JSON is validated before save — invalid documents are flagged in red.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {dirtyCount > 0 && (
              <Chip
                size="small"
                label={`${dirtyCount} unsaved`}
                sx={{
                  background: 'rgba(250, 135, 28, 0.12)',
                  color: '#a85b08',
                  border: '1px solid rgba(250, 135, 28, 0.36)',
                }}
              />
            )}
            {invalidCount > 0 && (
              <Chip
                size="small"
                color="error"
                label={`${invalidCount} invalid`}
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        <Paper sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              padding: 2.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(180deg, rgba(5, 35, 114, 0.04) 0%, transparent 100%)',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(180deg, rgba(5, 35, 114, 0.10) 0%, rgba(5, 35, 114, 0.04) 100%)',
                color: 'primary.main',
                border: '1px solid rgba(5, 35, 114, 0.10)',
              }}
            >
              <StorageOutlined fontSize="small" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography className="eyebrow" sx={{ color: 'text.secondary' }}>
                Collection
              </Typography>
              <Select
                value={this.state.table}
                fullWidth
                size="small"
                variant="standard"
                disableUnderline
                onChange={this.handleTableChange}
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: '-0.003em',
                  '& .MuiSelect-select': { paddingTop: 0.5, paddingBottom: 0.5, paddingLeft: 0 },
                }}
              >
                {this.state.tables.map(x => (
                  <MenuItem key={x} value={x} sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600 }}>
                    {x}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Chip
              icon={<DataObjectOutlined sx={{ fontSize: 16 }} />}
              label={`${this.state.items.length} documents`}
              size="small"
              sx={{
                background: 'rgba(5, 35, 114, 0.06)',
                color: 'primary.main',
                border: '1px solid rgba(5, 35, 114, 0.14)',
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          </Box>

          <Box sx={{ p: 2.5 }}>
            {this.state.items.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Loading documents…
              </Typography>
            )}
            {this.state.items.map((x: Record, i) => {
              const hasFocus = i === this.state.focusedIndex;
              let value = x.value;
              if (hasFocus) {
                try {
                  value = JSON.stringify(JSON.parse(x.value), null, '  ');
                } catch (e) {
                  // pretty-print fails silently when JSON is mid-edit
                }
              }
              return (
                <Box
                  key={x._id}
                  sx={{
                    mb: 2.5,
                    pb: 2.5,
                    borderBottom: i < this.state.items.length - 1 ? '1px dashed' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <DebouncedTextField
                    onFocus={this.handleFocus}
                    label={`Document  ${x._id}`}
                    fullWidth
                    margin="normal"
                    onChange={this.handleItemChange}
                    multiline
                    inputProps={{
                      'data-index': i,
                      style: { fontFamily: '"JetBrains Mono", monospace', fontSize: 13, lineHeight: 1.55 },
                    }}
                    value={value}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {x.dirty && (
                      <Chip
                        size="small"
                        label="Unsaved"
                        sx={{
                          background: 'rgba(250, 135, 28, 0.12)',
                          color: '#a85b08',
                          border: '1px solid rgba(250, 135, 28, 0.36)',
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: 10.5,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                        }}
                      />
                    )}
                    {!x.valid && (
                      <Chip size="small" color="error" label="Invalid JSON" variant="outlined" />
                    )}
                    {hasFocus && (
                      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button onClick={this.handleRevert} disabled={!x.dirty} size="small">
                          Revert
                        </Button>
                        <DangerButton
                          onClick={this.handleDelete}
                          button={<Button size="small">Delete</Button>}
                          loadedButton={<Button size="small" color="error" variant="outlined">Confirm delete</Button>}
                        />
                        <Button
                          onClick={this.handleUpdate}
                          disabled={!x.dirty || !x.valid}
                          size="small"
                          variant="contained"
                        >
                          Save changes
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      </MainPadding>
    );
  }
}
